import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ArrowRight, ChevronRight, Home } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useInternalLinkHandler(navigate) {
  return useCallback((e) => {
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href && href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    }
  }, [navigate]);
}

function RenderContent({ content, onLinkClick }) {
  const renderBlock = (block, i, level = 0) => {
    switch (block.type) {
      case 'p':
      case 'intro':
      case 'conclusion':
        return <p key={i} className={`text-zinc-300 leading-relaxed mb-4 ${block.type === 'intro' ? 'text-lg' : ''}`} onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text || '' }} />;
      case 'h2':
        return <h2 key={i} className="text-2xl font-semibold text-white mt-10 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.text}</h2>;
      case 'h3':
        return <h3 key={i} className="text-xl font-semibold text-white mt-8 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.text}</h3>;
      case 'section':
        return (
          <section key={i} className="mb-2">
            {block.h2 && <h2 className="text-2xl font-semibold text-white mt-10 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.h2}</h2>}
            {block.text && <p className="text-zinc-300 leading-relaxed mb-4" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text }} />}
            {(block.children || []).map((c, j) => renderBlock(c, `${i}-${j}`, level + 1))}
          </section>
        );
      case 'subsection':
        return (
          <div key={i}>
            {block.h3 && <h3 className="text-xl font-semibold text-white mt-8 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.h3}</h3>}
            {block.text && <p className="text-zinc-300 leading-relaxed mb-4" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text }} />}
            {(block.children || []).map((c, j) => renderBlock(c, `${i}-${j}`, level + 1))}
          </div>
        );
      case 'ul':
        return (
          <ul key={i} className="list-disc list-inside space-y-2 mb-4 pl-2">
            {(block.items || []).map((item, j) => (
              <li key={j} className="text-zinc-300 leading-relaxed" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        );
      case 'ol':
        return (
          <ol key={i} className="list-decimal list-inside space-y-2 mb-4 pl-2">
            {(block.items || []).map((item, j) => (
              <li key={j} className="text-zinc-300 leading-relaxed" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ol>
        );
      case 'cta': {
        const url = block.link || block.url || '/listings';
        return (
          <div key={i} className="mt-8 mb-4">
            <Link to={url} className="cta-button inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#b8933f] text-zinc-950 font-semibold px-6 py-3 text-sm transition-colors rounded-sm no-underline" data-testid="seo-page-cta">
              {block.text} <ArrowRight size={16} />
            </Link>
          </div>
        );
      }
      default:
        if (block.text) return <p key={i} className="text-zinc-300 leading-relaxed mb-4" onClick={onLinkClick} dangerouslySetInnerHTML={{ __html: block.text }} />;
        return null;
    }
  };
  return content.map((block, i) => renderBlock(block, i, 0));
}

export default function SEOPage() {
  const params = useParams();
  const navigate = useNavigate();
  // Support both /:slug (top-level SEO page) usage: params.slug is the pathname
  const slug = params.slug || params['*'] || window.location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const handleLinkClick = useInternalLinkHandler(navigate);

  useEffect(() => {
    if (!slug) return;
    let cancel = false;
    setLoading(true); setNotFound(false);
    axios.get(`${API}/seo-pages/${slug}`)
      .then((r) => {
        if (cancel) return;
        if (r.data?.redirect && r.data?.target) {
          if (r.data.target.startsWith('/')) navigate(r.data.target, { replace: true });
          else window.location.replace(r.data.target);
          return;
        }
        setPage(r.data);
      })
      .catch((e) => {
        if (cancel) return;
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [slug, navigate]);

  useSEO({
    title: page?.title,
    description: page?.meta_description,
    canonical: page ? `https://www.aerodeals.it${page.url_path || `/${page.slug}`}` : undefined,
  });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-20 flex items-center justify-center" data-testid="seo-page-loading">
        <Loader2 className="animate-spin text-zinc-500" size={20} />
      </div>
    );
  }
  if (notFound || !page) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-24 pb-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-3xl text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Pagina non trovata</h1>
          <p className="text-zinc-400 mb-6">La pagina che stai cercando non esiste o è stata rimossa.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-sm">
            <Home size={14} /> Torna alla home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="seo-page">
      <article className="max-w-3xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-8 font-mono" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#C5A059]">Home</Link>
          <ChevronRight size={12} />
          <span className="text-zinc-400 line-clamp-1">{page.h1 || page.title}</span>
        </nav>

        <header className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {page.h1 || page.title}
          </h1>
          {page.meta_description && (
            <p className="text-lg text-zinc-400 leading-relaxed">{page.meta_description}</p>
          )}
        </header>

        {page.image && (
          <div className="mb-10">
            <img src={page.image} alt={page.h1 || page.title} className="w-full h-auto" />
          </div>
        )}

        <div className="prose prose-invert max-w-none">
          <RenderContent content={page.content || []} onLinkClick={handleLinkClick} />
        </div>

        {/* FAQ */}
        {Array.isArray(page.faq) && page.faq.length > 0 && (
          <section className="mt-12 pt-8 border-t border-zinc-800">
            <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Domande frequenti</h2>
            <div className="space-y-4">
              {page.faq.map((f, i) => (
                <details key={i} className="border border-zinc-800 p-4 group">
                  <summary className="cursor-pointer text-white font-medium">{f.q || f.question}</summary>
                  <p className="text-zinc-400 mt-3 leading-relaxed">{f.a || f.answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Related posts */}
        {Array.isArray(page.related_posts) && page.related_posts.length > 0 && (
          <section className="mt-12 pt-8 border-t border-zinc-800">
            <h2 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Articoli correlati</h2>
            <ul className="space-y-2">
              {page.related_posts.map((rp, i) => (
                <li key={i}>
                  <Link to={rp.url || `/blog/${rp.slug}`} className="text-[#C5A059] hover:underline">{rp.title}</Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>

      {/* JSON-LD structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: page.h1 || page.title,
            description: page.meta_description,
            datePublished: page.published_at,
            dateModified: page.updated_at || page.published_at,
            author: { '@type': 'Organization', name: 'AeroDeals' },
            publisher: { '@type': 'Organization', name: 'AeroDeals', logo: { '@type': 'ImageObject', url: 'https://www.aerodeals.it/logo.png' } },
            mainEntityOfPage: `https://www.aerodeals.it${page.url_path || `/${page.slug}`}`,
          }),
        }}
      />
    </div>
  );
}
